import type {
  TaskGroupCard,
  TaskGroupPlatformCard
} from '@/features/tasks/api/taskGroupsApi'
import { isTaskProgressFrozen } from '@/features/tasks/utils/taskFrozen'

/** The one status a group card shows instead of each platform's own. */
export type TaskGroupStatus =
  | 'actionRequired'
  | 'inProgress'
  | 'frozen'
  | 'ended'

export type TaskGroupStatusResult = {
  status: TaskGroupStatus
  /** Frozen only: when the earliest frozen platform can be retried. */
  retryAt?: string
}

const ACTION = new Set(['reserved', 'moderation_error'])
const WORKING = new Set(['moderation', 'in_progress'])

/**
 * A platform's share in the group status; null for a platform the fan has
 * not taken. A failed platform the backend keeps in «My» can be retried,
 * one in «Closed» cannot.
 */
function platformStatus(
  platform: TaskGroupPlatformCard
): TaskGroupStatus | null {
  const progress = platform.progress
  if (!progress) {
    return null
  }
  const state = (progress.state ?? '').trim().toLowerCase()
  if (ACTION.has(state)) {
    return 'actionRequired'
  }
  if (WORKING.has(state)) {
    return 'inProgress'
  }
  if (state === 'failed' && platform.list === 'my') {
    return isTaskProgressFrozen(
      progress.type ?? platform.task.type,
      progress.is_frozen,
      progress.frozen_until
    )
      ? 'frozen'
      : 'actionRequired'
  }
  return 'ended'
}

// The product order (task progress statuses, § 5): anything the fan can do,
// then a freeze to wait out, then the system at work, ended last.
const PRIORITY: TaskGroupStatus[] = [
  'actionRequired',
  'frozen',
  'inProgress',
  'ended'
]

/** The group card's status; null while the fan has taken no platform. */
export function resolveTaskGroupStatus(
  group: TaskGroupCard
): TaskGroupStatusResult | null {
  const statuses = group.platforms.map(platform => ({
    platform,
    status: platformStatus(platform)
  }))
  const status = PRIORITY.find(candidate =>
    statuses.some(entry => entry.status === candidate)
  )
  if (!status) {
    return null
  }
  if (status !== 'frozen') {
    return { status }
  }
  const retryAt = statuses
    .filter(entry => entry.status === 'frozen')
    .map(entry => entry.platform.progress?.frozen_until ?? '')
    .filter(Boolean)
    .sort()[0]
  return retryAt ? { status, retryAt } : { status }
}
